import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Download, FileText } from "lucide-react";

export const RoPANoticeBuilder = ({ assessmentId }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/ropa/${assessmentId}/notice-builder`)
      .then(({ data: res }) => {
        setData(res);
        setSelected(res.notices?.[0]?.touchpoint || null);
      })
      .catch(() => toast.error("Could not build notices"))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  if (loading) return <Skeleton className="h-64 rounded-xl" data-testid="ropa-notice-loading" />;

  const notice = (data?.notices || []).find((n) => n.touchpoint === selected);

  const copy = async () => {
    await navigator.clipboard.writeText(notice.plain_text);
    toast.success("Notice copied to the clipboard");
  };

  const download = () => {
    const blob = new Blob([notice.plain_text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notice-${notice.touchpoint.replace(/\W+/g, "-").toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ropa-notice-builder">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
            Notice builder · DPDP Rule 3
          </h2>
          <p className="text-xs text-slate-400 mt-1" data-testid="ropa-notice-disclaimer">
            {data?.disclaimer}
          </p>
        </div>
        {(data?.notices?.length || 0) > 0 && (
          <div className="flex items-center gap-2">
            <Select value={selected || ""} onValueChange={setSelected}>
              <SelectTrigger className="w-[230px] h-9" data-testid="ropa-notice-touchpoint">
                <SelectValue placeholder="Select a consent touchpoint" />
              </SelectTrigger>
              <SelectContent>
                {data.notices.map((n) => (
                  <SelectItem key={n.touchpoint} value={n.touchpoint}>
                    {n.touchpoint} ({n.activity_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={copy} data-testid="ropa-notice-copy">
              <Copy size={14} className="mr-1.5" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={download} data-testid="ropa-notice-download">
              <Download size={14} className="mr-1.5" /> Download
            </Button>
          </div>
        )}
      </div>

      {!notice ? (
        <div className="py-12 text-center text-sm text-slate-500" data-testid="ropa-notice-empty">
          Approve at least one consent-based activity with a consent touchpoint to generate notice text.
        </div>
      ) : (
        <div className="mt-5 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <pre
              className="whitespace-pre-wrap text-xs leading-relaxed text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-[420px] overflow-auto"
              data-testid="ropa-notice-preview"
            >
              {notice.plain_text}
            </pre>
          </div>
          <div className="space-y-3 text-xs">
            {[
              ["Data items (i)", notice.data_items],
              ["Purposes (ii)", notice.purposes],
              ["Withdrawal & rights (iii)", notice.withdrawal_mechanisms],
              ["Records covered", notice.record_refs],
            ].map(([label, list]) => (
              <div key={label} className="border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1.5">
                  <FileText size={11} /> {label}
                </div>
                <ul className="mt-1.5 space-y-0.5 text-slate-600">
                  {(list || []).length === 0
                    ? <li className="text-slate-400">Not recorded</li>
                    : list.map((x, i) => <li key={`${x}-${i}`}>• {x}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const RoPAReports = ({ assessmentId, canExportDocx }) => {
  const [processors, setProcessors] = useState(null);
  const [transfers, setTransfers] = useState(null);

  useEffect(() => {
    api.get(`/ropa/${assessmentId}/processor-register`).then(({ data }) => setProcessors(data)).catch(() => {});
    api.get(`/ropa/${assessmentId}/cross-border-register`).then(({ data }) => setTransfers(data)).catch(() => {});
  }, [assessmentId]);

  const download = async (kind, scope) => {
    try {
      const token = localStorage.getItem("grc_token");
      const qs = kind === "csv" && scope === "all" ? "?scope=all" : "";
      const res = await fetch(`${API_BASE}/ropa/${assessmentId}/export/${kind}${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "docx" ? "CX0-RoPA.docx" : `CX0-RoPA-${scope === "all" ? "All" : "Approved"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} export downloaded`);
    } catch (e) {
      toast.error(`Could not generate the ${kind.toUpperCase()} export`);
    }
  };

  return (
    <div className="space-y-4" data-testid="ropa-reports-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h2 className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Regulator-ready exports</h2>
          <p className="text-xs text-slate-500 mt-1">Approved records only, with DPDP citations and annexures.</p>
        </div>
        {canExportDocx && (
          <Button size="sm" onClick={() => download("docx")} data-testid="ropa-export-docx" className="bg-[#008080] hover:bg-[#1a51db]">
            <Download size={14} className="mr-1.5" /> RoPA register (DOCX)
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => download("csv", "approved")} data-testid="ropa-export-csv">
          <Download size={14} className="mr-1.5" /> Export CSV (Approved)
        </Button>
        <Button size="sm" variant="outline" onClick={() => download("csv", "all")} data-testid="ropa-export-csv-all">
          <Download size={14} className="mr-1.5" /> Export CSV (All records)
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ropa-processor-register">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-[#003366]">Processor register</h3>
          <span className="text-[11px] font-mono text-slate-400">Section 8(2) evidence</span>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>{["Party", "Type", "Country", "DPA", "Expiry", "Activities", "Deletion on exit"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {(processors?.parties || []).map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`processor-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-[#003366]">{p.name}</td>
                  <td className="px-3 py-2 text-slate-600">{p.party_type}</td>
                  <td className="px-3 py-2 text-slate-600">{p.country}</td>
                  <td className="px-3 py-2">
                    <span style={{ color: p.dpa_status === "Missing" ? "#D32F2F" : p.dpa_status === "Signed" ? "#388E3C" : "#F57C00" }}
                      className="font-semibold">{p.dpa_status}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{p.dpa_expiry || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{p.linked_activity_count ?? 0}</td>
                  <td className="px-3 py-2 text-slate-600">{p.deletion_confirmation_on_exit ? "Yes" : "No"}</td>
                </tr>
              ))}
              {(processors?.parties || []).length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No third parties registered.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ropa-cross-border-register">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-[#003366]">Cross-border register</h3>
          <span className="text-[11px] font-mono text-slate-400">Rule 15 / Rule 13(4)</span>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>{["Record", "Status", "Countries", "Mechanism", "SDF restricted"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {(transfers?.transfers || []).map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`transfer-row-${t.id}`}>
                  <td className="px-3 py-2 font-medium text-[#003366]">{t.record_ref} {t.name}</td>
                  <td className="px-3 py-2 text-slate-600">{t.status}</td>
                  <td className="px-3 py-2 text-slate-600">{(t.recipient_countries || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{t.mechanism || "—"}</td>
                  <td className="px-3 py-2" style={{ color: t.sdf_localisation_restricted ? "#D32F2F" : "#64748B" }}>
                    {t.sdf_localisation_restricted ? "Yes" : "No"}
                  </td>
                </tr>
              ))}
              {(transfers?.transfers || []).length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No cross-border transfers recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RoPANoticeBuilder assessmentId={assessmentId} />
    </div>
  );
};
